import React, { useContext, useState } from "react";
import {
  Box,
  Typography,
  Skeleton,
  useTheme,
  useMediaQuery,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import TableRowsRoundedIcon from "@mui/icons-material/TableRowsRounded";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { Button } from "@mui/material";
import CardTitle from "./CardTitle";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseIcon from "@mui/icons-material/Close";
import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
  Cell,
} from "recharts";
import { GoogleMap, MarkerF, InfoWindowF } from "@react-google-maps/api";
import { AuthContext } from "../../context/AuthContextProvider";
import noOrgLogo from "../../assets/icons/noOrgLogo.svg";

/* Status meta — shared colour code for chart legend + table */
const STATUS_META = [
  { key: "normal", label: "Normal", color: "#53B253" },
  { key: "abnormal", label: "Abnormal", color: "#FFC46C" },
  { key: "critical", label: "Critical", color: "#FB8588" },
  { key: "retake", label: "Retake", color: "#C2C9FF" },
];

/* Table view of the same centre data shown in the bar chart */
const CentreTable = ({ data }) => (
  <TableContainer
    sx={{
      maxHeight: 320,
      borderRadius: "12px",
      border: "1px solid var(--app-border)",
    }}
  >
    <Table stickyHeader size="small">
      <TableHead>
        <TableRow>
          <TableCell
            sx={{
              fontWeight: 700,
              color: "var(--app-text)",
              backgroundColor: "var(--app-surface-2)",
              fontFamily: "Figtree",
            }}
          >
            Centre
          </TableCell>
          {STATUS_META.map((s) => (
            <TableCell
              key={s.key}
              align="center"
              sx={{
                fontWeight: 700,
                color: "var(--app-text)",
                backgroundColor: "var(--app-surface-2)",
                fontFamily: "Figtree",
                whiteSpace: "nowrap",
              }}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <Box
                  component="span"
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: s.color,
                  }}
                />
                {s.label}
              </Box>
            </TableCell>
          ))}
          <TableCell
            align="center"
            sx={{
              fontWeight: 700,
              color: "var(--app-text)",
              backgroundColor: "var(--app-surface-2)",
              fontFamily: "Figtree",
            }}
          >
            Total
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {data.map((row) => (
          <TableRow key={row.id} hover>
            <TableCell
              sx={{
                fontWeight: 600,
                color: "var(--app-text)",
                fontFamily: "Figtree",
              }}
            >
              {row.name}
            </TableCell>
            {STATUS_META.map((s) => (
              <TableCell
                key={s.key}
                align="center"
                sx={{ color: "var(--app-text)", fontFamily: "Figtree" }}
              >
                {row[s.key] ?? 0}
              </TableCell>
            ))}
            <TableCell
              align="center"
              sx={{
                fontWeight: 800,
                color: "var(--app-text)",
                fontFamily: "Figtree",
              }}
            >
              {row.total ?? 0}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const DashboardCentresSection = ({
  centreData,
  processedCentreData,
  loading,
  markers,
  selectedMarkerId,
  setSelectedMarkerId,
  mapRef,
  isLoaded,
  containerStyle,
  containerStyleFullScreen,
  CustomTooltip3,
  barsLoading,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { user } = useContext(AuthContext);

  // chart <-> table view toggle for the centres data
  const [view, setView] = useState("chart");
  const [openMapModal, setOpenMapModal] = useState(false);

  const handleOpenMap = () => setOpenMapModal(true);
  const handleCloseMap = () => setOpenMapModal(false);

  // Export the centre data as an Excel-openable workbook (no extra deps)
  const handleExportExcel = () => {
    if (!centreData || centreData.length === 0) return;

    const headers = ["Centre", ...STATUS_META.map((s) => s.label), "Total"];
    const rows = centreData.map((r) => [
      r.name,
      ...STATUS_META.map((s) => r[s.key] ?? 0),
      r.total ?? 0,
    ]);

    const headerHtml = headers
      .map(
        (h) =>
          `<th style="background:#F6F7F9;border:1px solid var(--app-border);padding:6px 10px;font-weight:700;text-align:left">${h}</th>`,
      )
      .join("");
    const bodyHtml = rows
      .map(
        (row) =>
          `<tr>${row
            .map(
              (c) =>
                `<td style="border:1px solid var(--app-border);padding:6px 10px">${c}</td>`,
            )
            .join("")}</tr>`,
      )
      .join("");

    const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="UTF-8"></head><body><table><thead><tr>${headerHtml}</tr></thead><tbody>${bodyHtml}</tbody></table></body></html>`;

    const blob = new Blob([html], {
      type: "application/vnd.ms-excel;charset=utf-8;",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "Centres_Report.xls";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  const renderMultilineTick = ({ x, y, payload }) => {
    const words = payload.value.split(" ");

    // Split into 2 lines max
    const firstLine = words.slice(0, 2).join(" ");
    const secondLine = words.slice(2).join(" ");

    return (
      <g transform={`translate(${x},${y + 10})`}>
        <text
          textAnchor="middle"
          fill="var(--app-text)"
          fontSize={isMobile ? 12 : 14}
          fontWeight={700}
        >
          <tspan x="0" dy="0">
            {firstLine}
          </tspan>
          {secondLine && (
            <tspan x="0" dy="14">
              {secondLine}
            </tspan>
          )}
        </text>
      </g>
    );
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
          pl: 3,
          pr: 1,
        }}
      >
        <CardTitle>Centres</CardTitle>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            onClick={handleExportExcel}
            disabled={!centreData || centreData.length === 0}
            startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              fontSize: "14px",
              fontFamily: "Figtree",
              color: "#5751E9",
              borderRadius: "8px",
              px: 1.25,
              "&:hover": { backgroundColor: "var(--app-hover)" },
              "&.Mui-disabled": { color: "#B9BCC2" },
            }}
          >
            Export Excel
          </Button>

          <ToggleButtonGroup
            value={view}
            exclusive
            size="small"
            onChange={(e, val) => val && setView(val)}
            sx={{
              backgroundColor: "#F4F5F7",
              borderRadius: "10px",
              p: "3px",
              "& .MuiToggleButton-root": {
                border: "none",
                borderRadius: "8px !important",
                px: 1.25,

                py: 0.5,
                color: "var(--app-text-muted)",
                textTransform: "none",
                gap: "6px",
                "&.Mui-selected": {
                  backgroundColor: "var(--app-surface)",
                  color: "#5751E9",
                  boxShadow: "var(--app-shadow)",
                  "&:hover": { backgroundColor: "var(--app-surface)" },
                },
              },
            }}
          >
            <ToggleButton value="chart" aria-label="chart view">
              <BarChartRoundedIcon sx={{ fontSize: 18 }} />
            </ToggleButton>
            <ToggleButton value="table" aria-label="table view">
              <TableRowsRoundedIcon sx={{ fontSize: 18 }} />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {centreData.length > 4 ? (
        <Box sx={{ width: "100%" }}>
          {/* <div
            style={{
              display: "flex",
              gap: 20,
              marginBottom: 10,
              fontWeight: 500,
              flexWrap: "wrap",
            }}
          >
            {[
              { color: "#53B253", label: "Normal" },
              { color: "#FFC46C", label: "Abnormal" },
              { color: "#FB8588", label: "Critical" },
              { color: "#C2C9FF", label: "Retake" },
            ].map((item, idx) => (
              <div
                key={idx}
                style={{ display: "flex", alignItems: "center", gap: 6 }}
              >
                <div
                  style={{
                    width: 20,
                    height: 6,
                    backgroundColor: item.color,
                    borderRadius: 4,
                  }}
                />
                {item.label}
              </div>
            ))}
          </div> */}

          {/* Table view */}
          {view === "table" ? (
            loading ? (
              <Skeleton
                variant="rectangular"
                width="100%"
                height={260}
                style={{ borderRadius: 12 }}
                animation="wave"
              />
            ) : (
              <CentreTable data={centreData} />
            )
          ) : (
            /* Scrollable Chart */
            <Box sx={{ width: "100%", overflowX: "auto" }}>
              <Box
                sx={{ minWidth: processedCentreData.length * 90, height: 300 }}
              >
                {loading ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "300px",
                      width: "100%",
                    }}
                  >
                    <Skeleton
                      variant="rectangular"
                      width="100%"
                      height={200}
                      style={{ borderRadius: 12 }}
                      animation="wave"
                    />
                  </Box>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                      data={processedCentreData}
                      onClick={(e) => {
                        const clickedCenterName = e?.activeLabel;
                        const markerIndex = markers.findIndex(
                          (m) => m.name === clickedCenterName,
                        );
                        if (markerIndex !== -1) {
                          setSelectedMarkerId(markerIndex);
                          const sel = markers[markerIndex];
                          mapRef.current?.panTo({ lat: sel.lat, lng: sel.lng });
                          mapRef.current?.setZoom(12);
                        }
                      }}
                      margin={{ top: 20, right: 20, left: -18, bottom: 30 }}
                      barCategoryGap={isMobile ? 15 : 30}
                    >
                      <CartesianGrid
                        stroke="var(--app-border)"
                        vertical={false}
                      />

                      <XAxis
                        dataKey="name"
                        axisLine={false}
                        tickLine={false}
                        tick={renderMultilineTick}
                      />
                      <YAxis
                        tick={{ fill: "var(--app-text)", fontSize: 14 }}
                        axisLine={false}
                        tickLine={false}
                        tickMargin={8}
                      />
                      <Tooltip
                        cursor={{ fill: "rgba(0, 0, 0, 0.04)" }}
                        content={<CustomTooltip3 />}
                      />

                      {/* Bars responsive width */}
                      {["normal", "abnormal", "critical", "retake"].map(
                        (key, i) => (
                          <Bar
                            key={i}
                            dataKey={key}
                            stackId="a"
                            barSize={isMobile ? 36 : 50}
                            activeBar={{ fillOpacity: 0.8 }}
                          >
                            {processedCentreData.map((entry, index) => (
                              <Cell
                                key={index}
                                fill={
                                  key === "normal"
                                    ? "#53B253"
                                    : key === "abnormal"
                                      ? "#FFC46C"
                                      : key === "critical"
                                        ? "#FB8588"
                                        : "#C2C9FF"
                                }
                                radius={
                                  entry.__topKey === key
                                    ? [8, 8, 0, 0]
                                    : [0, 0, 0, 0]
                                }
                              />
                            ))}
                          </Bar>
                        ),
                      )}
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </Box>
            </Box>
          )}

          {/* Map */}
          <Box
            sx={{
              mt: 4,
              position: "relative",
            }}
          >
            {isLoaded ? (
              <GoogleMap
                mapContainerStyle={containerStyle}
                center={
                  markers.length > 0 &&
                  !isNaN(markers[0].lat) &&
                  !isNaN(markers[0].lng)
                    ? {
                        lat: Number(markers[0].lat),
                        lng: Number(markers[0].lng),
                      }
                    : { lat: 20.5937, lng: 78.9629 }
                }
                zoom={markers.length > 0 ? 8 : 4}
                onLoad={(map) => (mapRef.current = map)}
                options={{
                  disableDefaultUI: true,
                  zoomControl: false,
                  streetViewControl: false,
                  fullscreenControl: false,
                }}
              >
                {" "}
                {markers.length > 0 &&
                  markers.map((marker, index) => (
                    <MarkerF
                      key={index}
                      position={{
                        lat: Number(marker.lat),
                        lng: Number(marker.lng),
                      }}
                      onClick={() => setSelectedMarkerId(index)}
                    >
                      {" "}
                      {selectedMarkerId === index &&
                        marker &&
                        !isNaN(Number(marker.lat)) &&
                        !isNaN(Number(marker.lng)) && (
                          <InfoWindowF
                            position={{
                              lat: Number(marker.lat),
                              lng: Number(marker.lng),
                            }}
                            onCloseClick={() => setSelectedMarkerId(null)}
                          >
                            <div>
                              <strong>{marker.name}</strong>
                              <br />
                              {marker.address}
                            </div>
                          </InfoWindowF>
                        )}{" "}
                    </MarkerF>
                  ))}{" "}
                <IconButton
                  onClick={handleOpenMap}
                  sx={{
                    position: "absolute",
                    bottom: 16,
                    right: 16,
                    width: 40,
                    height: 40,
                    bgcolor: "var(--app-surface)",
                    borderRadius: "4px",
                    p: 0.5,
                    zIndex: 1000,
                    boxShadow: "2px 2px 8px rgba(0,0,0,0.15)",
                    "&:hover": { bgcolor: "var(--app-surface-2)" },
                  }}
                >
                  {" "}
                  <OpenInFullIcon />
                </IconButton>
              </GoogleMap>
            ) : (
              <Box>Loading map...</Box>
            )}
          </Box>
        </Box>
      ) : (
        // fallback layout when fewer centres
        <Box sx={{ display: "flex", flexWrap: "wrap", width: "100%" }}>
          <Box sx={{ width: { xs: "100%", md: "50%" }, pt: "20px" }}>
            {isLoaded && (
              <GoogleMap
                mapContainerStyle={containerStyle}
                center={
                  markers.length > 0 &&
                  !isNaN(markers[0].lat) &&
                  !isNaN(markers[0].lng)
                    ? {
                        lat: Number(markers[0].lat),
                        lng: Number(markers[0].lng),
                      }
                    : { lat: 20.5937, lng: 78.9629 }
                }
                zoom={markers.length > 0 ? 8 : 4}
                onLoad={(map) => (mapRef.current = map)}
                options={{
                  disableDefaultUI: true,
                  zoomControl: false,
                  streetViewControl: false,
                  fullscreenControl: false,
                }}
              >
                {" "}
                {markers.length > 0 &&
                  markers.map((marker, index) => (
                    <MarkerF
                      key={index}
                      position={{
                        lat: Number(marker.lat),
                        lng: Number(marker.lng),
                      }}
                      onClick={() => {
                        setSelectedMarkerId(index);
                        mapRef.current?.panTo({
                          lat: Number(marker.lat),
                          lng: Number(marker.lng),
                        });
                      }}
                    >
                      {" "}
                      {selectedMarkerId === index &&
                        marker &&
                        !isNaN(Number(marker.lat)) &&
                        !isNaN(Number(marker.lng)) && (
                          <InfoWindowF
                            position={{
                              lat: Number(marker.lat),
                              lng: Number(marker.lng),
                            }}
                            onCloseClick={() => setSelectedMarkerId(null)}
                          >
                            <div>
                              <strong>{marker.name}</strong>
                              <br />
                              {marker.address}
                            </div>
                          </InfoWindowF>
                        )}
                    </MarkerF>
                  ))}
                <IconButton
                  onClick={handleOpenMap}
                  sx={{
                    position: "absolute",
                    bottom: 16,
                    right: 16,
                    width: 40,
                    height: 40,
                    bgcolor: "var(--app-surface)",
                    borderRadius: "4px",
                    p: 0.5,
                    zIndex: 1000,
                    boxShadow: "2px 2px 8px rgba(0,0,0,0.15)",
                    "&:hover": { bgcolor: "var(--app-surface-2)" },
                  }}
                >
                  {" "}
                  <OpenInFullIcon />
                </IconButton>
              </GoogleMap>
            )}
          </Box>

          <Box sx={{ width: { xs: "100%", md: "50%" }, pt: "20px" }}>
            {/* {centreData.length > 0 && (
              <div
                style={{
                  display: "flex",
                  gap: 24,
                  marginBottom: 18,
                  fontWeight: 500,
                  flexWrap: "wrap",
                  rowGap: 10,
                  alignItems: "center",
                }}
              >
                {[
                  { color: "#53B253", label: "Normal" },
                  { color: "#FFC46C", label: "Abnormal" },
                  { color: "#FB8588", label: "Critical" },
                  { color: "#C2C9FF", label: "Retake" },
                ].map((item, idx) => (
                  <div
                    key={idx}
                    style={{ display: "flex", alignItems: "center", gap: 6 }}
                  >
                    <div
                      style={{
                        width: 20,
                        height: 6,
                        backgroundColor: item.color,
                        borderRadius: 4,
                      }}
                    />
                    {item.label}
                  </div>
                ))}
              </div>
            )} */}

            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "300px",
                  width: "100%",
                }}
              >
                <Skeleton
                  variant="rectangular"
                  width="90%"
                  height={200}
                  style={{ borderRadius: 12 }}
                />
              </Box>
            ) : centreData.length === 0 ? (
              <Box
                sx={{
                  height: 300,
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img src={barsLoading} alt="No data" width={130} height={130} />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    fontSize: 16,
                    color: "var(--app-text-muted)",
                    mt: 1,
                    fontFamily: "Figtree",
                  }}
                >
                  No Centres
                </Typography>
              </Box>
            ) : view === "table" ? (
              <CentreTable data={centreData} />
            ) : (
              <Box
                sx={{
                  width: "100%",
                  overflowX: "auto",
                }}
              >
                <Box
                  sx={{
                    width: isMobile ? processedCentreData.length * 90 : "100%",
                    minWidth: isMobile ? 500 : "100%",
                    height: 320,
                    px: "10px",
                  }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={processedCentreData}
                      margin={{ top: 10, right: 20, left: 10, bottom: 25 }}
                      barGap={4}
                      barCategoryGap={isMobile ? 14 : 28}
                      onClick={(e) => {
                        const clickedCenterName = e?.activeLabel;
                        const markerIndex = markers.findIndex(
                          (m) => m.name === clickedCenterName,
                        );
                        if (markerIndex !== -1) {
                          setSelectedMarkerId(markerIndex);
                          const sel = markers[markerIndex];
                          mapRef.current?.panTo({ lat: sel.lat, lng: sel.lng });
                          mapRef.current?.setZoom(12);
                        }
                      }}
                    >
                      <CartesianGrid
                        stroke="var(--app-border)"
                        strokeWidth={1}
                        vertical={false}
                      />

                      <XAxis
                        dataKey="name"
                        axisLine={false}
                        tickLine={false}
                        tickMargin={12}
                        tick={renderMultilineTick}
                      />

                      <YAxis
                        tick={{ fill: "var(--app-text)", fontSize: 14 }}
                        axisLine={false}
                        tickLine={false}
                        tickMargin={6}
                      />

                      <Tooltip
                        cursor={{ fill: "rgba(0, 0, 0, 0.04)" }}
                        content={<CustomTooltip3 />}
                      />

                      {["normal", "abnormal", "critical", "retake"].map(
                        (key) => (
                          <Bar
                            key={key}
                            dataKey={key}
                            stackId="a"
                            barSize={isMobile ? 36 : 50}
                            activeBar={{ fillOpacity: 0.8 }}
                          >
                            {processedCentreData.map((entry, index) => (
                              <Cell
                                key={index}
                                fill={
                                  key === "normal"
                                    ? "#53B253"
                                    : key === "abnormal"
                                      ? "#FFC46C"
                                      : key === "critical"
                                        ? "#FB8588"
                                        : "#C2C9FF"
                                }
                                radius={
                                  entry.__topKey === key
                                    ? [8, 8, 0, 0]
                                    : [0, 0, 0, 0]
                                }
                              />
                            ))}
                          </Bar>
                        ),
                      )}
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      )}
      <Dialog
        open={openMapModal}
        onClose={handleCloseMap}
        fullWidth
        maxWidth="xl"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            backgroundColor: "var(--app-surface)",
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: "1px solid var(--app-border)",
            backgroundColor: "var(--app-surface)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            {/* Left Section (Logo + Name) */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flex: 1,
                minWidth: 0,
              }}
            >
              {/* Logo */}
              <Box
                sx={{
                  width: 45,
                  height: 45,
                  borderRadius: "5px",
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <img
                  src={user?.businessLogo || noOrgLogo}
                  alt="logo"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              </Box>

              {/* Business Name */}
              <Typography
                sx={{
                  fontSize: "15px",
                  fontWeight: 600,
                  color: "var(--app-text)",
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 2,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  wordBreak: "break-word",
                  lineHeight: "1.2em",
                }}
              >
                {user?.businessName}
              </Typography>
            </Box>

            {/* Close Button */}
            <IconButton
              onClick={handleCloseMap}
              sx={{
                ml: 2,
                color: "var(--app-text)",
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            p: 0,
            position: "relative",
          }}
        >
          <Box
            sx={{
              width: "100%",
              height: "80vh",
            }}
          >
            <GoogleMap
              mapContainerStyle={containerStyleFullScreen}
              center={
                markers.length > 0 &&
                !isNaN(markers[0].lat) &&
                !isNaN(markers[0].lng)
                  ? {
                      lat: Number(markers[0].lat),
                      lng: Number(markers[0].lng),
                    }
                  : { lat: 20.5937, lng: 78.9629 }
              }
              zoom={markers.length > 0 ? 8 : 4}
              onLoad={(map) => (mapRef.current = map)}
              options={{
                disableDefaultUI: true,
                zoomControl: false,
                streetViewControl: false,
                fullscreenControl: false,
              }}
            >
              {markers.length > 0 &&
                markers.map((marker, index) => (
                  <MarkerF
                    key={index}
                    position={{
                      lat: Number(marker.lat),
                      lng: Number(marker.lng),
                    }}
                    onClick={() => {
                      setSelectedMarkerId(index);
                      mapRef.current?.panTo({
                        lat: Number(marker.lat),
                        lng: Number(marker.lng),
                      });
                    }}
                  >
                    {selectedMarkerId === index &&
                      marker &&
                      !isNaN(Number(marker.lat)) &&
                      !isNaN(Number(marker.lng)) && (
                        <InfoWindowF
                          position={{
                            lat: Number(marker.lat),
                            lng: Number(marker.lng),
                          }}
                          onCloseClick={() => setSelectedMarkerId(null)}
                        >
                          <div>
                            <strong>{marker.name}</strong>
                            <br />
                            {marker.address}
                          </div>
                        </InfoWindowF>
                      )}
                  </MarkerF>
                ))}
            </GoogleMap>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DashboardCentresSection;
